"use client";

import BlurFade from "@/components/magicui/blur-fade";
import { useNextSectionScroll } from "@/lib/hooks/use-next-section-scroll";
import Section from "@/components/section";
import { Icons } from "@/components/icons";
import { Card, CardContent } from "@/components/ui/card";
import { Brain, Shield, Zap } from "lucide-react";
import { useTranslations } from "@/lib/hooks/use-translations";

const iconMap = {
  0: Brain,
  1: Zap,
  2: Shield
};

export default function Problem({ locale = "en" }: { locale?: "de" | "en" | "zh-CN" } = {}) {
  const handleNextSection = useNextSectionScroll("problem");
  const { t, locale: currentLocale } = useTranslations();

  const problems = t<Array<{ title: string; description: string }>>('pages.home.problem.problems');

  // For Chinese, align text to left; for other languages, center it
  const textAlignment = currentLocale === 'zh-CN' ? 'text-left' : 'text-center';
  const itemsAlignment = currentLocale === 'zh-CN' ? 'items-start' : 'items-center';

  // Determine contact page URL based on locale
  const getContactUrl = () => {
    const localeToUse = currentLocale || locale;
    if (localeToUse === "de") return "/de/kontakt";
    if (localeToUse === "zh-CN") return "/zh-CN/contact";
    return "/contact";
  };

  return (
    <Section
      id="problem"
      title={t('pages.home.problem.title')}
      subtitle={t('pages.home.problem.subtitle')}
      mainCta={{
        text: t('pages.home.hero.cta.getStarted'),
        href: getContactUrl(),
        icon: <Icons.logoIcon className="h-6 w-6" />
      }}
      secondaryCta={{
        text: t('pages.home.hero.cta.readMore'),
        onClick: handleNextSection
      }}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12 lg:pb-8">
        {problems.map((problem, index) => {
          const Icon = iconMap[index as keyof typeof iconMap];
          return (
            <BlurFade key={index} delay={0.2 + index * 0.2} inView>
              <Card className="bg-background border-none shadow-none hover:shadow transition duration-200">
                <CardContent className={`max-md:py-6 md:p-6 space-y-4 ${textAlignment} flex flex-col ${itemsAlignment}`}>
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon className="w-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold">{problem.title}</h3>
                  <p className="text-muted-foreground">{problem.description}</p>
                </CardContent>
              </Card>
            </BlurFade>
          );
        })}
      </div>
    </Section>
  );
}
